<template>
  <div>
    <v-autocomplete
      id="blockchain_export_format"
      :label="$tc('format')"
      :items="importFormats"
      item-text="name"
      item-value="format"
      v-model="format"
      :readonly="type === 'Edwards'"
    />

    <div>
      <v-autocomplete
        v-if="!isBip32"
        id="blockchain_export_version"
        :label="$tc('version')"
        :items="versions"
        item-text="name"
        item-value="version"
        v-model="version"
      />
    </div>

    <v-switch
      v-if="format === 1"
      id="blockchain_export_compressed"
      :label="$tc('compressed')"
      color="green"
      v-model="compressed"
    />

    <v-row no-gutters>
      <v-col cols="11">
        <v-textarea
          id="blockchain_export_outBlock"
          class="monospace"
          auto-grow
          row="6"
          row-height="25"
          label="Out Block"
          readonly
          v-model="outBlock"
          persistent-hint
          :hint="formatHint"
        />
      </v-col>
      <v-col cols="1">
        <v-row no-gutters>
          <v-col cols="12">
            <template v-if="isBip32Export">
              <v-tooltip left>
                <template #activator="{ on }">
                  <v-btn
                    id="blockchain_export_outBlock_format"
                    icon
                    v-on="on"
                    class="my-1"
                    color="contrasty"
                    @click="changeMaterialFormat"
                  >
                    <v-icon>check_circle_outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ formatTooltip }}</span>
              </v-tooltip>
            </template>
          </v-col>
          <v-col cols="12">
            <!-- CLIPBOARD -->
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  id="blockchain_outBlock_clipboard"
                  icon
                  v-on="on"
                  class="my-1"
                  color="contrasty"
                  v-clipboard:copy="outBlock"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc("copy") }}</span>
            </v-tooltip>
          </v-col>
          <v-col cols="12">
            <!-- SAVE -->
            <v-tooltip left>
              <template #activator="{ on }">
                <v-btn
                  id="blockchain_outBlock_saveFile"
                  icon
                  v-on="on"
                  class="my-1"
                  color="contrasty"
                  @click="saveToFile(outBlock)"
                >
                  <v-icon>save</v-icon>
                </v-btn>
              </template>
              <span>{{ $tc("save_to_file") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row justify="end" class="mx-4 mb-4 mt-2">
      <v-btn
        id="blockchain_export_btn"
        color="blue"
        :disabled="$v.$invalid"
        :dark="!$v.$invalid"
        @click="submit"
      >
        {{ $t("export") }}
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'

const XPRIV_TESTE_NET = '04358394'
const BIP32_XPRIV = 'BIP32 XPRIV'
const BIP32_EDWARDS_XPRIV = 'BIP32 Edwards XPRIV'
const EDWARDS = 'Edwards'
const SECP = 'SECP'

export default {
  emits: ['close'],
  props: {
    keyName: {
      type: String,
      required: true,
      default: null
    },
    keyInfo: {
      type: Object,
      required: true,
      default: null
    }
  },
  data () {
    return {
      versions: [{ name: this.$tc('main_net'), version: 128 }, { name: this.$tc('test_net'), version: 239 }],
      version: null,
      format: null,
      compressed: 0,
      outBlock: null,
      kinfoVersion: null,
      formatHex: true
    }
  },
  async created () {
    this.kinfoVersion = await this.getKinfoVersion()

    if (this.type === EDWARDS) {
      this.format = this.importFormats[0].format
    }
  },
  computed: {
    type () {
      return this.$store.getters['keys/getKeyType']({ type: this.keyInfo.type }).type
    },
    typeId () {
      return this.$store.getters['keys/getKeyType']({ type: this.keyInfo.type }).id
    },
    isBip32 () {
      return this.type === 'BIP32 XPRIV' || this.type === 'BIP32 Edwards XPRIV'
    },
    importFormats () {
      const formats = [
        {
          name: this.$tc('blockchain_export_wif'),
          format: 1
        },
        {
          name: this.$tc('blockchain_export__mini_key'),
          format: 2
        },
        {
          name: this.$tc('blockchain_export_hex'),
          format: 3
        },
        {
          name: this.$tc('blockchain_export_sec1'),
          format: 4
        },
        {
          name: this.$tc('blockchain_export_xpriv'),
          format: 5
        },
        {
          name: this.$tc('blockchain_export_ed25519_b64'),
          format: 6
        },
        {
          name: this.$tc('blockchain_export_ed25519_xpriv'),
          format: 7
        }
      ]

      const formatsBip32XprivMainNet = [
        {
          name: this.$tc('blockchain_export_wif'),
          format: 1
        },
        {
          name: this.$tc('blockchain_export_hex'),
          format: 3
        },
        {
          name: this.$tc('blockchain_export_sec1'),
          format: 4
        },
        {
          name: this.$tc('blockchain_export_xpriv'),
          format: 5
        }
      ]

      const formatsBip32XprivTestNet = [
        {
          name: this.$tc('blockchain_export_wif'),
          format: 1
        },
        {
          name: this.$tc('blockchain_export_hex'),
          format: 3
        },
        {
          name: this.$tc('blockchain_export_sec1'),
          format: 4
        }
      ]

      const formatsEd25519 = [
        {
          name: this.$tc('blockchain_export_ed25519_b64'),
          format: 6
        }
      ]

      const formatsSecp = [
        {
          name: this.$tc('blockchain_export_wif'),
          format: 1
        },
        {
          name: this.$tc('blockchain_export_hex'),
          format: 3
        },
        {
          name: this.$tc('blockchain_export_sec1'),
          format: 4
        }
      ]

      const formatsBip32Ed25519Xpriv = [
        {
          name: this.$tc('blockchain_export_xpriv'),
          format: 5
        },
        {
          name: this.$tc('blockchain_export_ed25519_b64'),
          format: 6
        },
        {
          name: this.$tc('blockchain_export_ed25519_xpriv'),
          format: 7
        }
      ]

      if (this.type === BIP32_XPRIV) {
        return this.kinfoVersion !== XPRIV_TESTE_NET ? formatsBip32XprivMainNet : formatsBip32XprivTestNet
      } else if (this.type === EDWARDS) {
        return formatsEd25519
      } else if (this.type === SECP) {
        return formatsSecp
      } else if (this.type === BIP32_EDWARDS_XPRIV) {
        return formatsBip32Ed25519Xpriv
      } else {
        return formats
      }
    },
    formatTooltip () {
      if (this.formatHex) {
        return this.$tc('format_string')
      } else {
        return this.$tc('format_hex')
      }
    },
    formatHint () {
      const exportFormat = [5, 6, 7]
      if (exportFormat.includes(this.format)) {
        if (this.formatHex) {
          return this.$tc('format_hex_hint')
        } else {
          return this.$tc('format_string_hint')
        }
      } else {
        return ''
      }
    },
    isBip32Export () {
      const exportFormat = [5, 6, 7]
      return exportFormat.includes(this.format)
    }
  },
  validations () {
    const validation = {}

    validation.format = {
      required
    }

    validation.version = {
      required
    }

    return validation
  },
  methods: {
    changeMaterialFormat () {
      const material = this.outBlock

      if (!this.formatHex) {
        let hex = ''
        for (let i = 0; i < material.length; i++) {
          hex += '' + material.charCodeAt(i).toString(16)
        }
        this.outBlock = hex
        this.formatHex = true
      } else {
        let string = ''
        for (let i = 0; i < material.length; i += 2) {
          string += String.fromCharCode(parseInt(material.substr(i, 2), 16))
        }

        this.outBlock = string
        this.formatHex = false
      }
    },
    async getKinfoVersion () {
      const kinfo = await this.$store.dispatch('hsm/bchain_kinfo', {
        body: {
          key: this.keyName
        }
      })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })

      return kinfo.x_ver
    },
    saveToFile (res) {
      if (res) {
        const FileSaver = require('file-saver')
        const blob = new Blob([res], { type: 'text/plain;charset=utf-8' })

        const formatName = this.importFormats.find(item => item.format === this.format).name.replace(/\s+/g, '')

        const fileName = this.keyName + formatName + '.bin'
        FileSaver.saveAs(blob, fileName)
      }
    },
    submit () {
      const compressedIntBool = this.compressed ? 1 : 0

      this.$store.dispatch('hsm/bchain_export', {
        body: {
          key: this.keyName,
          format: this.format,
          version: this.version,
          compressed: compressedIntBool
        }
      })
        .then((res) => {
          this.outBlock = res.data
          this.$notify.success({})
        })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
    }
  },
  watch: {
    kinfoVersion (val) {
      if (val !== XPRIV_TESTE_NET) {
        this.version = this.versions[0].version
      } else {
        this.version = this.versions[1].version
      }
    },
    format (val) {
      this.outBlock = null
    },
    version (val) {
      this.outBlock = null
    }
  }
}
</script>
